import {Injectable, OnDestroy, signal} from '@angular/core';
import {BehaviorSubject, firstValueFrom, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {IQuickMessageCategory, IQuickMessageItem, QUICK_MESSAGE_STATUS} from '../models/quick-message.model';
import {ConversationApiService} from '@shared/services/api/conversation/conversation-api.service';
import {AuthService} from '@core/authentication/auth.service';
import {DurableObjectService} from '@core/realtime/durable-object.service';
import {BUSINESS_EVENT_TYPE, PAGE_EVENT_TYPE} from '@shared/models/realtime/do-event.model';
import {IQmsgSearchSettings} from '../pipes/qmsg-filter.util';
import {UserSettingsService} from '@shared/services/global/user-settings.service';

/**
 * Đổi cấu trúc ở Cài đặt → đợi rồi mới tải lại: BE ghi ES không refresh, đọc ngay có thể ra dữ liệu cũ
 * (refresh mặc định của ES ~1s). Đồng thời gộp các sự kiện liên tiếp thành 1 lần tải.
 */
const QUICK_MESSAGE_RELOAD_DELAY_MS = 2000;

/** Danh sách đang hiển thị: tin của hội thoại (tự xác định nhóm) hoặc tin của 1 page chọn trong panel. */
interface IQuickMessageRequest {
  pageId: string;
  byPage: boolean;
  businessId?: string;
}

/**
 * Quản lý danh sách Tin nhắn Nhanh (Quick Message).
 *
 * Flow:
 * 1. Click hội thoại → loadQuickMessage(fanpageId)
 * → check cache theo page hoặc group
 * → nếu chưa có → handleQuickMsgGroup xác định page thuộc group không
 * - Có group → loadByBusinessId (cache theo group)
 * - Không group → loadByPage (cache theo page)
 * 2. Lần click sau → dùng cache, không gọi API
 * 3. Cài đặt Thêm/Sửa/Xoá/Bật-tắt → quick-message:changed (DO hoặc gọi trực tiếp cùng tab) → xoá cache
 *    và tải lại nền danh sách đang hiển thị (API chỉ trả tin đang bật); dữ liệu cũ giữ nguyên tới khi có mới
 */
@Injectable({providedIn: 'root'})
export class QuickMessageService implements OnDestroy {
  private _items$ = new BehaviorSubject<IQuickMessageItem[]>([]);
  public items$ = this._items$.asObservable();

  private _categories$ = new BehaviorSubject<IQuickMessageCategory[]>([]);
  public categories$ = this._categories$.asObservable();

  /** Cache pageId → items (FE13 cacheName: 'quickMsg_' + pageId) */
  private cacheByPage = new Map<string, IQuickMessageItem[]>();
  /** Cache groupId → items (FE13 cacheName: 'quickMsg_gr_' + groupId) */
  private cacheByGroup = new Map<string, IQuickMessageItem[]>();
  /** Cache businessId → categories */
  private categoryCacheByBusiness = new Map<string, IQuickMessageCategory[]>();
  /** pageId → groupId tin nhắn nhanh ('' = page không thuộc nhóm), để biết đọc cache nào */
  private groupIdByPage = new Map<string, string>();
  /** GroupId của page hiện tại (nếu có) */
  private currentGroupId = '';
  /** Signal: page hiện tại có group không — dùng để UI quyết định auto-select page filter */
  public readonly hasGroup = signal(false);
  /** Lượt tải đang chạy theo page — lượt gọi trùng dùng chung, không gọi lại API */
  private loadingByPage = new Map<string, Promise<IQuickMessageItem[]>>();
  /**
   * Lần tải gần nhất của màn đang hiển thị: để tải lại nền khi Cài đặt đổi, và để kết quả về muộn
   * của nguồn cũ (vd: vừa chuyển hội thoại) không ghi đè danh sách đang hiển thị.
   */
  private lastRequest: IQuickMessageRequest | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  /** [FE13 settingQuickMsgMatch] Cài đặt tìm kiếm theo field */
  public searchSettings: IQmsgSearchSettings = {
    shortcut: true,
    title: true,
    message: true,
  };

  /** Lượt nạp cài đặt tìm kiếm của phiên đăng nhập hiện tại (resetState → nạp lại). */
  private searchSettingsRequest: Promise<void> | null = null;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private api: ConversationApiService,
    private authService: AuthService,
    private doService: DurableObjectService,
    private userSettings: UserSettingsService,
  ) {
    // Khi auth thay đổi (chọn page, đổi business) → xoá cache, lần sau load lại từ API
    this.authService.onAuthChanged$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.resetState();
    });
  }

  ngOnDestroy(): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this._destroy$.next();
    this._destroy$.complete();
  }

  public setItems(items: IQuickMessageItem[]): void {
    this._items$.next(items || []);
  }

  public setCategories(items: IQuickMessageCategory[]): void {
    this._categories$.next(items || []);
  }

  /**
   * Load quick messages cho 1 page. Cache theo page hoặc group.
   *
   * Flow:
   * 1. Cache by page → trả về ngay
   * 2. Cache by group → filter items match pageId → trả về
   * 3. Chưa có cache → handleQuickMsgGroup xác định page có trong group không
   * - Có group → loadByBusinessId (cache group)
   * - Không → loadByPage (cache page)
   */
  public async loadQuickMessage(pageId: string, forceReload = false, businessIdOverride?: string): Promise<IQuickMessageItem[]> {
    if (!pageId) return [];
    this.lastRequest = {pageId, byPage: false, businessId: businessIdOverride};

    // Đang load page này → dùng chung lượt đang chạy. Trước đây trả [] nên nơi chờ kết quả
    // (vd: composer mobile) hiện danh sách rỗng khi trùng lúc với lượt tải khác.
    const loading = this.loadingByPage.get(pageId);
    if (!forceReload && loading) return loading;

    // Chỉ đọc cache của đúng nguồn của page (nhóm chứa page, hoặc chính page). Trước đây
    // lấy cache nhóm bất kỳ đang có → page khác nhóm / không thuộc nhóm hiện nhầm tin của nhóm đó.
    if (!forceReload && this.groupIdByPage.has(pageId)) {
      const groupId = this.groupIdByPage.get(pageId) || '';
      const cached = groupId ? this.cacheByGroup.get('gr_' + groupId) : this.cacheByPage.get(pageId);
      if (cached) {
        this.currentGroupId = groupId;
        this.hasGroup.set(!!groupId);
        this._items$.next([...cached]);
        return cached;
      }
    }

    const request = this.fetchQuickMessage(pageId, forceReload, businessIdOverride);
    this.loadingByPage.set(pageId, request);
    try {
      return await request;
    } finally {
      if (this.loadingByPage.get(pageId) === request) this.loadingByPage.delete(pageId);
    }
  }

  /**
   * Xác định page thuộc nhóm nào rồi tải đúng nguồn. hasGroup chỉ đổi khi đã biết kết quả (không
   * reset trước) để lượt tải lại nền không làm panel đang mở chớp giao diện.
   */
  private async fetchQuickMessage(
    pageId: string,
    forceReload: boolean,
    businessIdOverride?: string,
  ): Promise<IQuickMessageItem[]> {
    const businessId = businessIdOverride || (this.authService as any)?.businessCurrent?.id as string | undefined;

    if (businessId) {
      // FE13: handleQuickMsgGroup → kiểm tra page có trong group không
      try {
        const groupRes = await firstValueFrom(this.api.handleQuickMsgGroup({
          businessId,
          pageId,
          act: 'list',
        }));
        // API v2 handlegroup act=list trả về data.result
        const groupItems: any[] = (groupRes as any)?.data?.result ?? [];
        if (groupItems.length > 0) {
          const groupId = String(groupItems[0].id || '');
          this.groupIdByPage.set(pageId, groupId);
          if (this.isCurrentRequest(pageId, false)) {
            this.currentGroupId = groupId;
            this.hasGroup.set(true);
          }
          void this.loadCategories(String(businessId), forceReload);
          // Xoá cache page-level để tránh loadQuickMessage lần sau
          // tìm thấy dữ liệu page-level cũ (do loadByPage lưu) thay vì group items.
          // FE13: group items là nguồn chính xác duy nhất cho page có group.
          this.cacheByPage.delete(pageId);
          return this.loadByBusinessId(String(businessId), groupId, pageId, forceReload);
        }
      } catch { /* fallback → load theo page */
      }
    }

    // Không có group → load by page
    if (this.isCurrentRequest(pageId, false)) this.hasGroup.set(false);
    this.groupIdByPage.set(pageId, '');
    return this.fetchPageItems(pageId, false, forceReload);
  }

  /**
   * Load items theo business+groupId. Cache theo group để tái sử dụng
   * cho tất cả page trong cùng group.
   */
  private async loadByBusinessId(
    businessId: string,
    groupId: string,
    pageId: string,
    forceReload = false,
  ): Promise<IQuickMessageItem[]> {
    const cacheKey = 'gr_' + groupId;

    if (!forceReload && this.cacheByGroup.has(cacheKey)) {
      const cached = this.cacheByGroup.get(cacheKey) || [];
      this.emitIfCurrent(pageId, false, [...cached]);
      return cached;
    }

    try {
      const res = await firstValueFrom(this.api.getQuickMsgByBusinessId({
        businessId,
        groupId,
        status: QUICK_MESSAGE_STATUS.ACTIVE,
      }));
      const rawItems: any[] = (res as any)?.items ?? (res as any)?.data?.items ?? [];
      const processed = this.processRawItems(rawItems);

      this.cacheByGroup.set(cacheKey, processed);
      this.emitIfCurrent(pageId, false, processed);
      return processed;
    } catch {
      return [];
    }
  }

  /**
   * Load items theo pageId — panel chọn xem tin của 1 page cụ thể (FE13 changePageQmsg).
   * Cache theo page để tái sử dụng trong cùng session.
   */
  public async loadByPage(pageId: string, forceReload = false): Promise<IQuickMessageItem[]> {
    this.lastRequest = {pageId, byPage: true};
    return this.fetchPageItems(pageId, true, forceReload);
  }

  /** Dùng API /business/quickmessage/list với pageIds, cache theo page. */
  private async fetchPageItems(pageId: string, byPage: boolean, forceReload = false): Promise<IQuickMessageItem[]> {
    if (!forceReload && this.cacheByPage.has(pageId)) {
      const cached = this.cacheByPage.get(pageId) || [];
      this.emitIfCurrent(pageId, byPage, [...cached]);
      return cached;
    }

    try {
      // FE13 dùng /quickmessage/all (BE luôn lọc tin đang bật); API list cần truyền status,
      // không thì tin đã tắt ở Cài đặt vẫn hiện trong hội thoại.
      const res = await firstValueFrom(this.api.getQuickMessagesByPageIds([pageId], {
        status: QUICK_MESSAGE_STATUS.ACTIVE,
      }));
      // API v2 quickmessage/list trả về data.result
      const rawItems: any[] = (res as any)?.data?.result ?? [];
      const processed = this.processRawItems(rawItems);

      this.cacheByPage.set(pageId, processed);
      this.emitIfCurrent(pageId, byPage, processed);
      return processed;
    } catch {
      return [];
    }
  }

  private isCurrentRequest(pageId: string, byPage: boolean): boolean {
    const last = this.lastRequest;
    return !last || (last.pageId === pageId && last.byPage === byPage);
  }

  /** Kết quả về muộn của nguồn cũ (vd: vừa chuyển hội thoại) chỉ lưu cache, không ghi đè danh sách đang hiển thị. */
  private emitIfCurrent(pageId: string, byPage: boolean, items: IQuickMessageItem[]): void {
    if (this.isCurrentRequest(pageId, byPage)) {
      this._items$.next(items);
    }
  }

  /**
   * [FE13 processRawItems] Xử lý dữ liệu thô từ API giống FE13:
   * - Thêm '/' vào shortcut nếu chưa có
   * - Tự động tạo shortcut /(index+1) nếu item không có phím tắt (FE13 default behavior)
   * - Tách ats → image/video
   */
  private processRawItems(rawList: any[]): IQuickMessageItem[] {
    return rawList.map((r: any, index: number) => {
      const autoShortcut = !r.shortcut;
      if (Object.prototype.hasOwnProperty.call(r, 'shortcut') && r.shortcut && !String(r.shortcut).startsWith('/')) {
        r.shortcut = '/' + r.shortcut;
      } else if (!Object.prototype.hasOwnProperty.call(r, 'shortcut') || !r.shortcut) {
        // FE13: auto-generate shortcut theo số thứ tự
        r.shortcut = '/' + (index + 1);
      }

      if (Object.prototype.hasOwnProperty.call(r, 'ats') && Array.isArray(r.ats)) {
        r.ats.forEach((v: any) => (v.idQms = r.id));
      }

      return {
        id: String(r.id || ''),
        shortcut: String(r.shortcut || ''),
        title: String(r.title || ''),
        message: String(r.message || ''),
        backgroundColor: String(r.backgroundColor || r.bgColor || ''),
        color: String(r.color || ''),
        ats: (r.ats as unknown[]) || [],
        image: (r.image as unknown[]) || (Array.isArray(r.ats) ? r.ats.filter((i: any) => !i.type || i.type === 'image') : []),
        video: (r.video as unknown[]) || (Array.isArray(r.ats) ? r.ats.filter((i: any) => i.type === 'video') : []),
        pageId: String(r.pageId || r.fanpageId || ''),
        categoryId: r.categoryId ? String(r.categoryId) : undefined,
        childs: (r.childs as any[]) || undefined,
        requireAddress: r.requireAddress ? Number(r.requireAddress) : undefined,
        sendPriority: r.sendPriority ? String(r.sendPriority) : undefined,
        templates: Array.isArray(r.templates) ? r.templates : undefined,
        formId: r.formId ? String(r.formId) : undefined,
        // Giữ để vá cache theo sự kiện Cài đặt mà vẫn đúng thứ tự/đánh số như lúc tải từ API
        order: r.order != null && r.order !== '' ? Number(r.order) : undefined,
        createdTime: r.createdTime ? Number(r.createdTime) : undefined,
        autoShortcut: autoShortcut || undefined,
      };
    });
  }

  /**
   * [FE13 loadQuickMsgCategories] Load categories.
   */
  public async loadCategories(businessId: string, forceReload = false): Promise<IQuickMessageCategory[]> {
    if (!businessId) return [];

    if (!forceReload && this.categoryCacheByBusiness.has(businessId)) {
      const cached = this.categoryCacheByBusiness.get(businessId) || [];
      this._categories$.next(cached);
      return cached;
    }

    try {
      const res = await firstValueFrom(this.api.getQuickMessageCategories({
        businessId,
        act: 'list',
      }));
      const rawItems: any[] = (res as any)?.data?.result ?? [];
      const fetched: IQuickMessageCategory[] = rawItems.map((entry, index) => ({
        id: String(entry.id || ''),
        name: String(entry.name || ''),
        order: Number(entry.order || index),
      }));
      this.categoryCacheByBusiness.set(businessId, fetched);
      this._categories$.next(fetched);
      return fetched;
    } catch {
      return [];
    }
  }

  // ── Đồng bộ với trang Cài đặt ──────────────────────────────────────────────

  /**
   * Trang Cài đặt báo tin nhắn nhanh vừa đổi: tab hiện tại xử lý ngay (không chắc DO gửi lại cho
   * chính tab đã bắn), rồi bắn quick-message:changed cho page hoặc doanh nghiệp để tab/nhân viên
   * khác cập nhật. Payload giữ dạng FE13 để nhân viên còn dùng bản cũ vẫn đọc được.
   */
  public publishChange(target: {pageId?: string; businessId?: string}, payload: Record<string, unknown>): void {
    const data = {type: 'Qmsg', ...payload};
    this.handleQuickMessageChanged(data);
    if (!this.doService.isConfigured()) return;
    if (target.pageId) {
      this.doService.sendPageEvent(target.pageId, {type: PAGE_EVENT_TYPE.QUICK_MESSAGE_CHANGED, payload: data});
    } else if (target.businessId) {
      this.doService.sendBusinessEvent(target.businessId, {type: BUSINESS_EVENT_TYPE.QUICK_MESSAGE_CHANGED, payload: data});
    }
  }

  /**
   * Nhận quick-message:changed: DO event của page/business, hoặc publishChange ở cùng tab.
   * Payload FE13: { type: 'Qmsg', value, idsRemove?, action?, messages? }; reload = đổi cấu trúc.
   *
   * BE lưu tin bằng ES update không refresh → gọi lại API ngay sau khi lưu vẫn có thể ra dữ liệu cũ
   * (bật/tắt xong vẫn thấy như cũ tới khi F5). Vì vậy thay đổi có đủ dữ liệu trong payload (bật/tắt,
   * sửa, thêm, xoá, sao chép sang page) được vá thẳng vào cache và danh sách đang hiển thị, không gọi API.
   * Chỉ thay đổi cấu trúc (page vào/ra nhóm, xoá nhóm, import, sao chép sang nhóm, danh mục) mới xoá
   * cache và tải lại sau khi ES kịp refresh.
   */
  public handleQuickMessageChanged(data: any): void {
    if (!data || data.type !== 'Qmsg') return;
    const changedKeys = this.patchCaches(data);
    if (changedKeys) {
      this.emitDisplayedIfChanged(changedKeys);
      return;
    }
    this.clearCaches();
    this.scheduleRefresh();
  }

  /**
   * Vá cache theo payload. Key thay đổi: 'gr_<groupId>' (cache nhóm) hoặc 'page_<pageId>' (cache page).
   * @returns null khi payload không đủ dữ liệu để vá (đổi cấu trúc) → cần tải lại.
   */
  private patchCaches(data: any): Set<string> | null {
    if (data.reload) return null;
    const value = data.value && typeof data.value === 'object' ? data.value : {};
    const changed = new Set<string>();

    // Sao chép sang page (FE13 copyQmsg): action 1 = thay thế, 2 = thêm
    if (data.action && Array.isArray(data.messages)) {
      const pageId = String(value.fanpageId || '');
      if (!pageId) return null;
      const cached = this.cacheByPage.get(pageId);
      if (cached) {
        const copied = this.processRawItems(data.messages
          .filter((item: any) => this.isActiveQuickMessage(item))
          .map((item: any) => ({...item})));
        const copiedIds = new Set(copied.map(item => item.id));
        const kept = Number(data.action) === 1 ? [] : cached.filter(item => !copiedIds.has(item.id));
        this.cacheByPage.set(pageId, this.sortQuickMessages([...kept, ...copied]));
        changed.add('page_' + pageId);
      }
      return changed;
    }

    const idsRemove: string[] = Array.isArray(data.idsRemove) ? data.idsRemove.map(String) : [];
    if (idsRemove.length) {
      this.removeFromCaches(new Set(idsRemove), changed);
      return changed;
    }
    if (value.id) {
      this.upsertInCaches(value, changed);
      return changed;
    }
    return null;
  }

  /**
   * Tin vừa lưu/bật-tắt: tin tắt bỏ khỏi mọi cache (hội thoại chỉ dùng tin đang bật); tin bật/sửa cập
   * nhật ở cache chứa nó và chuyển sang đúng cache khi đổi nhóm/page. Không biết nguồn (thiếu cả
   * groupId lẫn fanpageId) thì chỉ sửa tại chỗ.
   */
  private upsertInCaches(value: any, changed: Set<string>): void {
    const id = String(value.id);
    if (!this.isActiveQuickMessage(value)) {
      this.removeFromCaches(new Set([id]), changed);
      return;
    }
    const groupKey = value.groupId ? 'gr_' + value.groupId : '';
    const pageKey = !value.groupId && value.fanpageId ? String(value.fanpageId) : '';
    const hasTarget = !!(groupKey || pageKey);
    const patch = (list: IQuickMessageItem[], isTarget: boolean): IQuickMessageItem[] | null => {
      const index = list.findIndex(item => item.id === id);
      if (index < 0 && !isTarget) return null;
      if (hasTarget && !isTarget) return this.sortQuickMessages(list.filter(item => item.id !== id));
      const item = this.processRawItems([{...value}])[0];
      return this.sortQuickMessages(index >= 0 ? list.map(entry => entry.id === id ? item : entry) : [...list, item]);
    };

    for (const [key, list] of this.cacheByGroup) {
      const next = patch(list, key === groupKey);
      if (next) {
        this.cacheByGroup.set(key, next);
        changed.add(key);
      }
    }
    for (const [pageId, list] of this.cacheByPage) {
      const next = patch(list, pageId === pageKey);
      if (next) {
        this.cacheByPage.set(pageId, next);
        changed.add('page_' + pageId);
      }
    }
  }

  private removeFromCaches(ids: Set<string>, changed: Set<string>): void {
    for (const [key, list] of this.cacheByGroup) {
      if (list.some(item => ids.has(item.id))) {
        this.cacheByGroup.set(key, this.sortQuickMessages(list.filter(item => !ids.has(item.id))));
        changed.add(key);
      }
    }
    for (const [pageId, list] of this.cacheByPage) {
      if (list.some(item => ids.has(item.id))) {
        this.cacheByPage.set(pageId, this.sortQuickMessages(list.filter(item => !ids.has(item.id))));
        changed.add('page_' + pageId);
      }
    }
  }

  private isActiveQuickMessage(item: any): boolean {
    return Number(item?.status || QUICK_MESSAGE_STATUS.ACTIVE) === QUICK_MESSAGE_STATUS.ACTIVE;
  }

  /**
   * Sắp xếp như BE (order rồi createdTime tăng dần, thiếu giá trị xếp cuối); tin không có phím tắt
   * đánh số lại theo vị trí như lúc tải (processRawItems).
   */
  private sortQuickMessages(list: IQuickMessageItem[]): IQuickMessageItem[] {
    const rank = (value: number | undefined) => value ?? Number.MAX_SAFE_INTEGER;
    return [...list]
      .sort((a, b) => rank(a.order) - rank(b.order) || rank(a.createdTime) - rank(b.createdTime))
      .map((item, index) => item.autoShortcut ? {...item, shortcut: '/' + (index + 1)} : item);
  }

  /** Danh sách đang hiển thị nằm trong cache vừa vá → phát lại ngay để panel đang mở cập nhật. */
  private emitDisplayedIfChanged(changed: Set<string>): void {
    const last = this.lastRequest;
    if (!last) return;
    const groupId = last.byPage ? '' : this.groupIdByPage.get(last.pageId);
    if (groupId === undefined) return;
    const key = groupId ? 'gr_' + groupId : 'page_' + last.pageId;
    const list = groupId ? this.cacheByGroup.get(key) : this.cacheByPage.get(last.pageId);
    if (list && changed.has(key)) {
      this._items$.next([...list]);
    }
  }

  /**
   * Đổi cấu trúc: còn màn đang dùng danh sách (composer, private reply... đang subscribe items$) →
   * tải lại nền lượt gần nhất, đợi ES refresh để không đọc phải dữ liệu cũ. Danh sách cũ giữ nguyên
   * tới khi có dữ liệu mới (không đóng panel, không xoá trắng, không loading). Không màn nào dùng
   * (vd: đang ở trang Cài đặt) thì để lần mở panel sau tự tải.
   */
  private scheduleRefresh(): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      const last = this.lastRequest;
      if (!last || !this._items$.observed) return;
      void (last.byPage
        ? this.loadByPage(last.pageId, true)
        : this.loadQuickMessage(last.pageId, true, last.businessId));
    }, QUICK_MESSAGE_RELOAD_DELAY_MS);
  }

  private clearCaches(): void {
    this.cacheByPage.clear();
    this.cacheByGroup.clear();
    this.categoryCacheByBusiness.clear();
    this.groupIdByPage.clear();
  }

  // ── Reset state (giống TagService.resetState) ───────────────────────────

  /**
   * Reset state — gọi khi đổi business hoặc logout.
   */
  public resetState(): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
    this._items$.next([]);
    this._categories$.next([]);
    this.clearCaches();
    this.currentGroupId = '';
    this.lastRequest = null;
    this.loadingByPage.clear();
    this.hasGroup.set(false);
    this.searchSettingsRequest = null;
  }

  // ── Search Settings (FE13 settingQuickMsgMatch) ──────────────────────────

  /**
   * [FE13 getSettingUserQuickMsg] Load cài đặt tìm kiếm từ server.
   */
  public loadSearchSettings(): Promise<void> {
    // Ô nhập bị tạo lại mỗi lần đổi hội thoại: chỉ nạp một lần mỗi phiên, sau đó giữ state cục bộ
    // (updateSearchSetting ghi thẳng vào searchSettings) như FE13 nạp một lần khi vào trang.
    this.searchSettingsRequest ??= this.fetchSearchSettings();
    return this.searchSettingsRequest;
  }

  private async fetchSearchSettings(): Promise<void> {
    try {
      // Dùng chung cache /user/setting của UserSettingsService (đã nạp khi đăng nhập), không gọi API riêng.
      await this.userSettings.loadSettings();
      const setting: any = this.userSettings.setting();
      if (!setting) {
        // API lỗi: không giữ lượt nạp hỏng, lần mở ô nhập sau thử lại.
        this.searchSettingsRequest = null;
        return;
      }
      if (setting?.searchQmsgMatch) {
        this.searchSettings.shortcut = setting.searchQmsgMatch.shortcut == '1' || setting.searchQmsgMatch.shortcut === true;
        this.searchSettings.title = setting.searchQmsgMatch.title == '1' || setting.searchQmsgMatch.title === true;
        this.searchSettings.message = setting.searchQmsgMatch.message == '1' || setting.searchQmsgMatch.message === true;
      }
    } catch {
      // giữ nguyên default, lần sau thử lại
      this.searchSettingsRequest = null;
    }
  }

  /**
   * [FE13 handleChangeSettingSearchQmsg] Cập nhật + lưu setting.
   */
  public async updateSearchSetting(field: 'shortcut' | 'title' | 'message', checked: boolean): Promise<void> {
    this.searchSettings[field] = checked;
    const params = {
      qmsg: JSON.stringify({
        shortcut: this.searchSettings.shortcut ? 1 : 0,
        title: this.searchSettings.title ? 1 : 0,
        message: this.searchSettings.message ? 1 : 0,
      }),
    };
    try {
      await firstValueFrom(this.api.updateSettingQmsg(params));
    } catch {
      // silent
    }
  }

  /** Get tất cả items hiện tại (cho parent component filter) */
  get currentItems(): IQuickMessageItem[] {
    return this._items$.getValue();
  }

  /** [FE13] Lọc theo shortcut */
  public filterByShortcut(keyword: string, pageId?: string): IQuickMessageItem[] {
    const list = this._items$.getValue();
    const normalized = (keyword || '').replace(/^\//, '').toLowerCase().trim();

    return list.filter(item => {
      const passPage = !pageId || item.pageId === pageId;
      const shortcut = (item.shortcut || '').replace(/^\//, '').toLowerCase();
      return passPage && shortcut.includes(normalized);
    });
  }

  /** [FE13] Tìm kiếm theo message/title/shortcut */
  public search(keyword: string, pageId?: string): IQuickMessageItem[] {
    const list = this._items$.getValue();
    const k = (keyword || '').toLowerCase();

    return list.filter(i => {
      const okPage = !pageId || i.pageId === pageId;
      const text = (i.message || '') + ' ' + (i.title || '') + ' ' + (i.shortcut || '');
      return okPage && text.toLowerCase().includes(k);
    });
  }
}
